import React, { useState } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { ActivityPointsMultiplierTierGroupDto } from '../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';

interface DeleteActivityPointsRuleDialogProps {
    open: boolean;
    onClose: () => void;
    rule: ActivityPointsMultiplierTierGroupDto | null;
    onSuccess: () => void;
}

export const DeleteActivityPointsRuleDialog: React.FC<
    DeleteActivityPointsRuleDialogProps
> = ({ open, onClose, rule, onSuccess }) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDelete = async () => {
        if (!rule?.id) return;

        setLoading(true);
        setError(null);

        try {
            await fitnessActivityClient.admin.activityPointsMultiplierTierGroupControllerDelete(
                rule.id
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to delete activity points rule:', err);
            setError(
                'Failed to delete activity points rule. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Activity Points Rule</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-2">
                    <p className="text-sm">
                        Are you sure you want to delete this activity points
                        multiplier tier group?
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Activity Type: <strong>{rule?.activityType}</strong>
                        {rule?.workoutType && (
                            <>
                                , Workout Type:{' '}
                                <strong>{rule?.workoutType}</strong>
                            </>
                        )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        This action cannot be undone. The tier group will be
                        permanently removed.
                    </p>

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={loading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
